import { useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Check, Plus, Trash2 } from "lucide-react"
import { InterneAufgabeModal } from "@/components/portal/InterneAufgabeModal"
import { mitGesetztemWert } from "@/lib/auswahlOptionen"
import { RueckrufBadge, type RueckrufEintrag } from "@/components/portal/RueckrufStatus"

/**
 * Interne Aufgaben — das, was das Büro für sich selbst notiert (#33).
 *
 * Gemeldet am 24.08.2026 von Marlene: „wir brauchen eine interne Section wo wir
 * aufgaben für uns selbst anlegen, gerade für solche Kunden, die keine Lizenz
 * für uns kaufen". Nachgeschoben am 25.08. von Paul: „Man braucht hier
 * außerdem die nutzer auswahl und dass jeder nutzer seine aufgaben dann sehen
 * kann."
 *
 * 🔴 Diese Aufgaben gehen NICHT nach HERO. Das ist der ganze Zweck: für einen
 * Interessenten ohne Vorgang gibt es dort nichts, woran sie hängen könnten.
 *
 * 🔑 Die Zuständigkeit ist eine SICHT, keine Berechtigung. Jeder sieht alles —
 * „Meine" ist ein Filter, kein Zaun. Eine interne Notizliste, in der man die
 * Aufgaben der Kollegin nicht sieht, hilft dem Büro nicht.
 */

type Aufgabe = {
  id: number
  tenant_slug: string | null
  titel: string
  notiz: string | null
  faellig_am: string | null
  erledigt_am: string | null
  zustaendig: string | null
  prioritaet: "hoch" | "normal" | "niedrig" | null
  email_id: number | null
  call_id: number | null
  whatsapp_nachricht_id?: number | null
  hero_project_id: number | null
  created_at: string
  /** #151: neuester dokumentierter Rückruf am KI-Telefon-Anruf, aus dem die
   *  Aufgabe entstand (Backend liest die Spur `(Voice-Call …)` der Notiz) */
  rueckruf?: RueckrufEintrag | null
}

/** Die drei Stufen (#39). `null` zählt als „normal" — sonst bildeten die
 *  Altlasten einen eigenen Block ganz oben oder ganz unten. */
const PRIO_STUFEN = ["hoch", "normal", "niedrig"] as const
const PRIO_TITEL: Record<string, string> = {
  hoch: "Hoch", normal: "Normal", niedrig: "Niedrig",
}
const prioVon = (a: Aufgabe) => a.prioritaet || "normal"

/** Die Herkunfts-Links („Mail", „Anruf", „Vorgang") sehen bewusst gleich aus —
 *  sie tun dasselbe, nur an einem anderen Ort. */
const herkunftStil = {
  fontSize: 10.5, fontWeight: 600, padding: "1px 6px", borderRadius: 999,
  background: "var(--bg-muted, rgba(148,163,184,0.14))", color: "var(--text-muted)",
  border: "1px solid var(--border-color)", textDecoration: "none",
  flexShrink: 0,
} as const

const HEUTE = () => new Date().toISOString().slice(0, 10)

function faelligText(a: Aufgabe): { text: string; dringend: boolean } | null {
  if (!a.faellig_am) return null
  const heute = HEUTE()
  const datum = a.faellig_am.split("-").reverse().join(".")
  if (a.erledigt_am) return { text: datum, dringend: false }
  if (a.faellig_am < heute) return { text: `${datum} · überfällig`, dringend: true }
  if (a.faellig_am === heute) return { text: "heute fällig", dringend: true }
  return { text: datum, dringend: false }
}

export default function InterneAufgabenPage() {
  return <InterneAufgabenListe />
}

/**
 * Die Liste selbst — auf der eigenen Seite UND als Reiter im Review (#13).
 *
 * 🔑 Eine Liste, zwei Orte: gewünscht war „Interne Aufgaben in Review"
 * (Marlene 08.10.2026, „Beides" = sehen/abhaken und anlegen). Ein zweiter
 * Nachbau im Review würde beim nächsten Feld wieder auseinanderlaufen.
 *
 * `tenant`: im Review gilt der dort gewählte Betrieb. `eingebettet`: ohne
 * eigene Seitenüberschrift — die Review-Seite hat schon eine.
 */
export function InterneAufgabenListe({ tenant, eingebettet = false }: {
  tenant?: string | null
  eingebettet?: boolean
} = {}) {
  const qc = useQueryClient()
  const [nurOffene, setNurOffene] = useState(true)
  const [nurMeine, setNurMeine] = useState(false)
  const [neuOffen, setNeuOffen] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const liste = useQuery<{ items: Aufgabe[]; ich: string }>({
    queryKey: ["portal-aufgaben", nurOffene, nurMeine, tenant || ""],
    queryFn: async () => {
      const p = new URLSearchParams()
      if (tenant) p.set("tenant", tenant)
      if (nurOffene) p.set("offen", "true")
      if (nurMeine) p.set("meine", "true")
      const r = await fetch(`/api/v1/portal-aufgaben?${p}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    staleTime: 1000 * 10,
  })

  const zustaendige = useQuery<{ items: string[] }>({
    queryKey: ["portal-aufgaben-zustaendige"],
    queryFn: async () => {
      const r = await fetch("/api/v1/portal-aufgaben/zustaendige", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    staleTime: 1000 * 60 * 10,
  })

  const frisch = () => qc.invalidateQueries({ queryKey: ["portal-aufgaben"] })
  const kaputt = (e: unknown) => setFehler(e instanceof Error ? e.message : String(e))

  const aendern = useMutation({
    mutationFn: async (a: { id: number; body: Record<string, unknown> }) => {
      const r = await fetch(`/api/v1/portal-aufgaben/${a.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(a.body),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
    },
    onSuccess: frisch,
    onError: kaputt,
  })

  const loeschen = useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`/api/v1/portal-aufgaben/${id}`, {
        method: "DELETE", credentials: "include",
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
    },
    onSuccess: frisch,
    onError: kaputt,
  })

  const items = liste.data?.items ?? []
  const ich = liste.data?.ich ?? ""

  // Nur fuer die Anzeige: der Slug allein sagt niemandem etwas.
  const mandanten = useQuery<{ items: { slug: string; name: string }[] }>({
    queryKey: ["tenants-dropdown"],
    queryFn: async () => {
      const r = await fetch("/api/v1/tenants", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    staleTime: 1000 * 60 * 5,
  })

  // Gliederung nach PRIORITÄT, darin nach Fälligkeit (#39, Entscheidung Paul
  // vom 26.08.2026). Vorher war die Fälligkeit die Hauptgliederung.
  //
  // 🔴 Folge, bewusst in Kauf genommen: Überfälliges steht nicht mehr
  // automatisch oben — eine niedrig priorisierte Aufgabe, die seit zwei Wochen
  // fällig ist, landet unter „Niedrig". Gegenmittel ist die rote Auszeichnung
  // in der Zeile (`faelligText`), damit sie beim Durchscrollen anspringt.
  //
  // Die Reihenfolge INNERHALB einer Gruppe liefert schon der Server
  // (`liste_portal_aufgaben`); hier wird nur gebündelt, nicht neu sortiert —
  // sonst sortierte jede Ansicht anders.
  const gruppen = useMemo(() => {
    const offen = items.filter((a) => !a.erledigt_am)
    return [
      ...PRIO_STUFEN.map((stufe) => ({
        titel: PRIO_TITEL[stufe],
        dringend: stufe === "hoch",
        items: offen.filter((a) => prioVon(a) === stufe),
      })),
      { titel: "Erledigt", dringend: false, items: items.filter((a) => a.erledigt_am) },
    ].filter((g) => g.items.length > 0)
  }, [items])

  const pille = (aktiv: boolean) => ({
    fontSize: 12, padding: "4px 11px", borderRadius: 999, cursor: "pointer",
    background: aktiv ? "#EEF2FF" : "transparent",
    color: aktiv ? "#3C3489" : "var(--text-muted)",
    border: `1px solid ${aktiv ? "#C7D2FE" : "var(--border-color)"}`,
    fontWeight: aktiv ? 600 : 500,
  }) as const

  return (
    <div style={eingebettet ? { padding: "12px 0", maxWidth: 760 } : { padding: "20px 24px", maxWidth: 760 }}>
      <div style={{
        display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        gap: 12, flexWrap: "wrap", marginBottom: 16,
      }}>
        <div>
          {!eingebettet && (
            <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 3 }}>Interne Aufgaben</h1>
          )}
          <p style={{ fontSize: 12.5, color: "var(--text-muted)", margin: 0, maxWidth: 520 }}>
            Nur für uns — diese gehen nicht nach HERO. Jeder sieht alles;
            „Meine" ist ein Filter, kein Zaun.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setNeuOffen(true)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13,
            fontWeight: 500, padding: "8px 14px", borderRadius: 8, border: "none",
            background: "#3C3489", color: "#fff", cursor: "pointer", flexShrink: 0,
          }}
        >
          <Plus size={15} /> Neue Aufgabe
        </button>
      </div>

      <div style={{ display: "flex", gap: 7, marginBottom: 14, flexWrap: "wrap" }}>
        <button type="button" onClick={() => setNurOffene(!nurOffene)} style={pille(nurOffene)}>
          Nur offene
        </button>
        <button type="button" onClick={() => setNurMeine(!nurMeine)} style={pille(nurMeine)}>
          Meine{ich && ` (${ich})`}
        </button>
      </div>

      {fehler && (
        <div style={{ fontSize: 12.5, color: "var(--destructive)", marginBottom: 10 }}>{fehler}</div>
      )}

      {liste.isLoading ? (
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Lädt …</div>
      ) : items.length === 0 ? (
        <div style={{
          padding: "28px 20px", textAlign: "center", borderRadius: 10,
          border: "1px dashed var(--border-color)", color: "var(--text-muted)", fontSize: 13,
        }}>
          {nurMeine ? "Für dich ist nichts offen."
            : nurOffene ? "Nichts offen." : "Noch keine internen Aufgaben."}
        </div>
      ) : (
        gruppen.map((g) => (
          <section key={g.titel} style={{ marginBottom: 20 }}>
            <div style={{
              fontSize: 11.5, fontWeight: 600, textTransform: "uppercase",
              letterSpacing: 0.4, color: g.dringend ? "var(--destructive)" : "var(--text-muted)",
              marginBottom: 6,
            }}>
              {g.titel} <span style={{ fontWeight: 400 }}>· {g.items.length}</span>
            </div>
            <ul style={{
              listStyle: "none", padding: 0, margin: 0, borderRadius: 10,
              border: "1px solid var(--border-color)", overflow: "hidden",
              background: "var(--bg-card)",
            }}>
              {g.items.map((a, i) => {
                const f = faelligText(a)
                return (
                  <li key={a.id} style={{
                    display: "flex", alignItems: "flex-start", gap: 11,
                    padding: "10px 12px",
                    borderTop: i === 0 ? "none" : "1px solid var(--border-color)",
                    opacity: a.erledigt_am ? 0.5 : 1,
                  }}>
                    <button
                      type="button"
                      title={a.erledigt_am ? "Wieder öffnen" : "Erledigt"}
                      onClick={() => aendern.mutate({ id: a.id, body: { erledigt: !a.erledigt_am } })}
                      style={{
                        flexShrink: 0, marginTop: 1, width: 19, height: 19, borderRadius: "50%",
                        border: `1.5px solid ${a.erledigt_am ? "#059669" : "var(--border-color)"}`,
                        background: a.erledigt_am ? "#059669" : "transparent",
                        color: a.erledigt_am ? "#fff" : "var(--text-muted)",
                        cursor: "pointer", display: "inline-flex",
                        alignItems: "center", justifyContent: "center", padding: 0,
                      }}
                    >
                      {a.erledigt_am ? <Check size={11} /> : null}
                    </button>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13.5,
                        textDecoration: a.erledigt_am ? "line-through" : "none",
                        display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap",
                      }}>
                        {/* 🔴 Der Titel MUSS ein eigenes Element sein. Als
                            nackter Textknoten in einem Flex-Kasten wird er auf
                            Wortbreite gequetscht und bricht nach jedem Wort um —
                            beim Durchklicken auf dev gesehen (26.08.2026):
                            „Testlauf / Durchklicken / — bitte / loeschen", mit
                            der Marke quer darüber. */}
                        <span style={{ minWidth: 0 }}>{a.titel}</span>
                        {/* Prioritätsmarke: nur bei „hoch", sonst ist die
                            Liste voller Schilder, die nichts sagen (#39). */}
                        {prioVon(a) === "hoch" && (
                          <span style={{
                            fontSize: 10.5, fontWeight: 700, padding: "1px 6px",
                            borderRadius: 999, background: "#FEE2E2", color: "#991B1B",
                            border: "1px solid #FCA5A5", textDecoration: "none",
                            flexShrink: 0,
                          }}>Hoch</span>
                        )}
                        {/* Woraus die Aufgabe entstanden ist — der Rückweg zur
                            Quelle. Ohne den ist die Verknüpfung angelegt, aber
                            unsichtbar (#39). */}
                        {a.email_id && (
                          <a href={`/emails?open=${a.email_id}${a.tenant_slug ? `&tenant=${encodeURIComponent(a.tenant_slug)}` : ""}`}
                             title="Zur Mail, aus der die Aufgabe entstand"
                             style={herkunftStil}>Mail</a>
                        )}
                        {a.call_id && (
                          <a href={`/anrufe?open=${a.call_id}`}
                             title="Zum Anruf, aus dem die Aufgabe entstand"
                             style={herkunftStil}>Anruf</a>
                        )}
                        {/* #158: vom Inhaber per WhatsApp beauftragt, automatisch angelegt */}
                        {a.whatsapp_nachricht_id && (
                          <a href="/whatsapp"
                             title="Automatisch über WhatsApp angelegt — zum WhatsApp-Verlauf"
                             style={herkunftStil}>WhatsApp</a>
                        )}
                        {a.hero_project_id && (
                          <a href={`/projekte/${a.hero_project_id}`}
                             title="Zum verknüpften HERO-Vorgang"
                             style={herkunftStil}>Vorgang</a>
                        )}
                        {/* Einen Mandanten zuweisen zu können und ihn dann
                            nirgends zu sehen wäre die halbe Sache (#39). */}
                        {a.tenant_slug && (
                          <span style={{
                            fontSize: 10.5, fontWeight: 600, padding: "1px 6px",
                            borderRadius: 999, background: "#EEF2FF", color: "#3C3489",
                            border: "1px solid #C7D2FE", textDecoration: "none",
                            flexShrink: 0,
                          }}>
                            {mandantName(mandanten.data?.items, a.tenant_slug)}
                          </span>
                        )}
                      </div>
                      {a.rueckruf && (
                        <div style={{ marginTop: 3 }}><RueckrufBadge eintrag={a.rueckruf} /></div>
                      )}
                      {a.notiz && (
                        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                          {a.notiz}
                        </div>
                      )}
                      {f && (
                        <div style={{
                          fontSize: 11.5, marginTop: 3,
                          color: f.dringend ? "var(--destructive)" : "var(--text-muted)",
                          fontWeight: f.dringend ? 600 : 400,
                        }}>
                          {f.text}
                        </div>
                      )}
                    </div>

                    {/* Zuständigkeit direkt in der Zeile umhängen — dafür extra
                        einen Dialog zu öffnen wäre für ein Feld zu viel. */}
                    {/* Priorität direkt in der Zeile umhängen — wie die
                        Zuständigkeit. Dafür einen Dialog zu öffnen wäre für
                        ein Feld zu viel. */}
                    <select
                      value={prioVon(a)}
                      title="Priorität"
                      aria-label="Priorität"
                      onChange={(e) => aendern.mutate({
                        id: a.id, body: { prioritaet: e.target.value },
                      })}
                      style={{
                        // 🔴 Feste Breite. Ohne sie dehnt sich ein `<select>`
                        // mit `flexShrink: 0` auf den ganzen freien Platz — auf
                        // dev gemessen: 686 px, und der Titel daneben blieb bei
                        // 0 px und brach nach jedem Buchstaben um (26.08.2026).
                        flexShrink: 0, width: 88,
                        fontSize: 11.5, padding: "3px 6px", borderRadius: 6,
                        border: "1px solid var(--border-color)", background: "transparent",
                        color: "var(--text)", cursor: "pointer",
                      }}
                    >
                      {PRIO_STUFEN.map((s) => (
                        <option key={s} value={s}>{PRIO_TITEL[s]}</option>
                      ))}
                    </select>

                    <select
                      value={a.zustaendig || ""}
                      title="Zuständig"
                      onChange={(e) => aendern.mutate({
                        id: a.id, body: { zustaendig: e.target.value },
                      })}
                      style={{
                        flexShrink: 0, fontSize: 11.5, padding: "3px 6px", borderRadius: 6,
                        border: "1px solid var(--border-color)", background: "transparent",
                        color: a.zustaendig ? "var(--text)" : "var(--text-muted)",
                        maxWidth: 130, cursor: "pointer",
                      }}
                    >
                      <option value="">— niemand —</option>
                      {/* 🔴 Der gesetzte Wert MUSS als Option dabeisein, auch
                          wenn die Namensliste noch lädt, fehlschlägt oder ihn
                          nicht mehr kennt. Sonst zeigt das Feld „— niemand —",
                          obwohl jemand zuständig ist — und das ist von „wirklich
                          niemand" nicht zu unterscheiden. */}
                      {mitGesetztemWert(zustaendige.data?.items ?? [], a.zustaendig).map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>

                    <button type="button" title="Löschen" onClick={() => loeschen.mutate(a.id)}
                            style={{ flexShrink: 0, background: "none", border: "none",
                                     cursor: "pointer", color: "var(--text-muted)", padding: 3 }}>
                      <Trash2 size={13} />
                    </button>
                  </li>
                )
              })}
            </ul>
          </section>
        ))
      )}

      <InterneAufgabeModal open={neuOffen} onClose={() => setNeuOffen(false)} tenantSlug={tenant || null} />
    </div>
  )
}

/** Anzeigename eines Mandanten — faellt auf den Slug zurueck, statt nichts zu
 *  zeigen: ein leeres Schild waere schlechter als ein technischer Name. */
function mandantName(
  liste: { slug: string; name: string }[] | undefined, slug: string,
): string {
  return liste?.find((m) => m.slug === slug)?.name || slug
}
